import type { PresentationInput } from '../presentation-input';
import { fetchImageBlob } from './fetch-image';
import { isPng, jpegSize, pngSize } from './image-size';
import type { ScreenTemplates } from './insights-screen';
import type { FontKey } from './layout';
import type { PdfImage } from './pdf-kit';
import type { RenderAssets } from './render-presentation';

/**
 * Браузерная часть сборки: загрузка ассетов макета и картинок пользователя,
 * приведение их к JPEG/PNG нужного размера и сама сборка PDF.
 */

const FONT_URLS: Record<FontKey, string> = {
  rubik: new URL('../../assets/fonts/Rubik-Regular.ttf', import.meta.url).href,
  rubikSemiBold: new URL('../../assets/fonts/Rubik-SemiBold.ttf', import.meta.url).href,
  golos: new URL('../../assets/fonts/GolosText-Regular.ttf', import.meta.url).href,
  golosSemiBold: new URL('../../assets/fonts/GolosText-SemiBold.ttf', import.meta.url).href,
  oswaldBold: new URL('../../assets/fonts/Oswald-Bold.ttf', import.meta.url).href,
  bricolageSemiBold: new URL('../../assets/fonts/BricolageGrotesque-SemiBold.ttf', import.meta.url).href,
  // Шрифт экранов статистики — системный шрифт iOS: цифры на скриншоте
  // приложения и наши должны быть одинаковыми. В файле только кириллица и
  // цифры, остальное вырезано.
  sf: new URL('../../assets/fonts/SFPro-Semibold.ttf', import.meta.url).href,
};

const IMAGE_URLS = {
  phoneFrame: new URL('../../assets/images/phone-frame.png', import.meta.url).href,
  laptop: new URL('../../assets/images/laptop.png', import.meta.url).href,
  hand: new URL('../../assets/images/hand-cursor.png', import.meta.url).href,
  iconMail: new URL('../../assets/images/icon-mail.png', import.meta.url).href,
  iconPhone: new URL('../../assets/images/icon-phone.png', import.meta.url).href,
  iconTelegram: new URL('../../assets/images/icon-telegram.png', import.meta.url).href,
  swirl: new URL('../../assets/images/outro-swirl.png', import.meta.url).href,
  coverBackground: new URL('../../assets/images/cover-background.jpg', import.meta.url).href,
  tagShadow: new URL('../../assets/images/tag-shadow.png', import.meta.url).href,
} as const;

const SCREEN_URLS: Record<keyof ScreenTemplates, string> = {
  reel: new URL('../../assets/images/insights/screen-reel.png', import.meta.url).href,
  post: new URL('../../assets/images/insights/screen-post.png', import.meta.url).href,
};

// Ограничения размера картинок пользователя: с запасом ×2–2,5 к месту на слайде.
const PHOTO_MAX_SIDE = 2000;
/** Превью на экране статистики — с ноготь: на слайде это 112 pt. */
const THUMB_MAX_SIDE = 500;
const SCREENSHOT_MAX_SIDE = 2400;
const LOGO_MAX_SIDE = 1200;
const JPEG_QUALITY = 0.9;

async function fetchBytes(url: string): Promise<Uint8Array> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Не удалось загрузить ${url}: ${res.status}`);
  return new Uint8Array(await res.arrayBuffer());
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function bytesToImage(data: Uint8Array, alias?: string): PdfImage {
  const png = isPng(data);
  return { data, format: png ? 'PNG' : 'JPEG', ...(png ? pngSize(data) : jpegSize(data)), alias };
}

let assetsPromise: Promise<RenderAssets> | null = null;

/** Ассеты макета грузятся один раз за сессию страницы. */
function loadAssets(): Promise<RenderAssets> {
  assetsPromise ??= (async () => {
    const fontEntries = await Promise.all(
      (Object.entries(FONT_URLS) as Array<[FontKey, string]>).map(
        async ([key, url]) => [key, toBase64(await fetchBytes(url))] as const,
      ),
    );
    const loadImages = async (urls: Record<string, string>, prefix: string) =>
      Object.fromEntries(
        await Promise.all(
          Object.entries(urls).map(
            async ([key, url]) => [key, bytesToImage(await fetchBytes(url), prefix + key)] as const,
          ),
        ),
      );
    const [images, screens] = await Promise.all([
      loadImages(IMAGE_URLS, ''),
      loadImages(SCREEN_URLS, 'screen-'),
    ]);
    return {
      fonts: Object.fromEntries(fontEntries) as Record<FontKey, string>,
      ...(images as Omit<RenderAssets, 'fonts' | 'screens'>),
      screens: screens as ScreenTemplates,
    };
  })().catch((err) => {
    assetsPromise = null;
    throw err;
  });
  return assetsPromise;
}

/**
 * Шаблон и jsPDF начинают грузиться, пока сервер собирает статистику, —
 * к сборке PDF они уже на месте. Ошибка всплывёт позже, в самой сборке.
 */
export function preloadPresentationAssets(): void {
  loadAssets().catch(() => {});
  import('jspdf').catch(() => {});
  import('./render-presentation').catch(() => {});
}

async function decode(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function canvasToBytes(canvas: HTMLCanvasElement, type: string): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('Не удалось подготовить картинку'));
        blob.arrayBuffer().then((buf) => resolve(new Uint8Array(buf)), reject);
      },
      type,
      JPEG_QUALITY,
    );
  });
}

/**
 * Любая картинка (JPEG, PNG, WebP, HEIC там, где браузер его понимает, SVG)
 * → JPEG или PNG не больше maxSide по длинной стороне.
 */
async function prepareImage(
  source: Blob | string,
  opts: { maxSide: number; keepAlpha: boolean },
): Promise<PdfImage> {
  const blob = typeof source === 'string' ? await fetchImageBlob(source) : source;
  const img = await decode(blob);
  // У SVG без width/height натуральный размер может быть нулевым.
  const srcW = img.naturalWidth || 1024;
  const srcH = img.naturalHeight || 1024;
  const scale = Math.min(1, opts.maxSide / Math.max(srcW, srcH));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(srcW * scale));
  canvas.height = Math.max(1, Math.round(srcH * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas недоступен');
  if (!opts.keepAlpha) {
    // JPEG не хранит прозрачность — подложка кремовая, как карточка слайда.
    ctx.fillStyle = '#FFFBEA';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const type = opts.keepAlpha ? 'image/png' : 'image/jpeg';
  const data = await canvasToBytes(canvas, type);
  return { data, format: opts.keepAlpha ? 'PNG' : 'JPEG', width: canvas.width, height: canvas.height };
}

/**
 * Экран телефона в макете вытянут (9:19,5): при заполнении пост ленты 4:5
 * терял ~40% ширины, рилс 9:16 — края подписей. Такие картинки вписываются
 * по ширине, а сверху и снизу — размытая копия, как в самом Instagram.
 */
const SCREEN_ASPECT = 262.408 / 559.188;
// Всё, что шире экрана (рилсы 9:16 тоже), вписывается целиком — обрезка
// срезала бы подписи на ролике.
const FIT_ABOVE_ASPECT = SCREEN_ASPECT * 1.02;
const SCREEN_RENDER_WIDTH = 1000;

async function prepareScreenImage(source: Blob | string): Promise<PdfImage> {
  const blob = typeof source === 'string' ? await fetchImageBlob(source) : source;
  const img = await decode(blob);
  const srcW = img.naturalWidth || 1024;
  const srcH = img.naturalHeight || 1024;
  if (srcW / srcH <= FIT_ABOVE_ASPECT) {
    return prepareImage(blob, { maxSide: PHOTO_MAX_SIDE, keepAlpha: false });
  }

  const canvas = document.createElement('canvas');
  canvas.width = SCREEN_RENDER_WIDTH;
  canvas.height = Math.round(SCREEN_RENDER_WIDTH / SCREEN_ASPECT);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas недоступен');

  // Размытие без ctx.filter (его нет в части Safari): ужать до пары десятков
  // пикселей и растянуть обратно со сглаживанием.
  const cover = Math.max(canvas.width / srcW, canvas.height / srcH);
  const tiny = document.createElement('canvas');
  tiny.width = 16;
  tiny.height = Math.max(1, Math.round((16 * canvas.height) / canvas.width));
  const tctx = tiny.getContext('2d');
  if (!tctx) throw new Error('Canvas недоступен');
  const tscale = tiny.width / canvas.width;
  tctx.drawImage(
    img,
    ((canvas.width - srcW * cover) / 2) * tscale,
    ((canvas.height - srcH * cover) / 2) * tscale,
    srcW * cover * tscale,
    srcH * cover * tscale,
  );
  // Растягиваем в два шага: сразу в полный размер остаются заметные «квадраты».
  const mid = document.createElement('canvas');
  mid.width = tiny.width * 4;
  mid.height = tiny.height * 4;
  const mctx = mid.getContext('2d');
  if (!mctx) throw new Error('Canvas недоступен');
  mctx.imageSmoothingQuality = 'high';
  mctx.drawImage(tiny, 0, 0, mid.width, mid.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(mid, 0, 0, canvas.width, canvas.height);

  const fitH = (canvas.width / srcW) * srcH;
  ctx.drawImage(img, 0, (canvas.height - fitH) / 2, canvas.width, fitH);

  const data = await canvasToBytes(canvas, 'image/jpeg');
  return { data, format: 'JPEG', width: canvas.width, height: canvas.height };
}

/** Превью нужно в двух видах, а качать его надо один раз. */
function sourceBlob(source: Blob | string, cache: Map<string, Promise<Blob>>): Promise<Blob> {
  if (typeof source !== 'string') return Promise.resolve(source);
  let pending = cache.get(source);
  if (!pending) {
    pending = fetchImageBlob(source);
    cache.set(source, pending);
  }
  return pending;
}

/** Превью для экрана статистики: сама картинка, без полей и размытия. */
function optionalThumb(
  source: Blob | string | null,
  cache: Map<Blob | string, Promise<PdfImage | null>>,
  blobs: Map<string, Promise<Blob>>,
): Promise<PdfImage | null> {
  if (!source) return Promise.resolve(null);
  let pending = cache.get(source);
  if (!pending) {
    pending = sourceBlob(source, blobs)
      .then((blob) => prepareImage(blob, { maxSide: THUMB_MAX_SIDE, keepAlpha: false }))
      .catch(() => null);
    cache.set(source, pending);
  }
  return pending;
}

/** Картинка для экрана телефона; сбой — заглушка, а не падение всего PDF. */
function optionalScreenImage(
  source: Blob | string | null,
  cache: Map<Blob | string, Promise<PdfImage | null>>,
  blobs: Map<string, Promise<Blob>>,
): Promise<PdfImage | null> {
  if (!source) return Promise.resolve(null);
  let pending = cache.get(source);
  if (!pending) {
    pending = sourceBlob(source, blobs)
      .then(prepareScreenImage)
      .catch((err) => {
        console.warn('[Presentation] картинка не загрузилась:', source, err);
        return null;
      });
    cache.set(source, pending);
  }
  return pending;
}

/**
 * Картинки и шрифты, готовые к сборке файла. Шаг общий у PDF и SVG: оба
 * рисуют один макет, и расходиться им нельзя.
 */
export type PreparedPresentation = {
  assets: RenderAssets;
  logo: PdfImage | null;
  cover: PdfImage | null;
  posts: Array<PdfImage | null>;
  thumbnails: Array<PdfImage | null>;
  screenshots: PdfImage[];
};

export async function preparePresentation(
  input: PresentationInput,
  onProgress: (message: string) => void = () => {},
): Promise<PreparedPresentation> {
  onProgress('Загрузка шаблона...');
  const assets = await loadAssets();

  onProgress('Подготовка картинок...');
  // Одна и та же картинка (превью первого поста на титуле) готовится один раз.
  const photos = new Map<Blob | string, Promise<PdfImage | null>>();
  const thumbs = new Map<Blob | string, Promise<PdfImage | null>>();
  const blobs = new Map<string, Promise<Blob>>();

  const logoPromise = input.logo
    ? prepareImage(input.logo, { maxSide: LOGO_MAX_SIDE, keepAlpha: true })
    : Promise.resolve(null);
  // Свою картинку для титула выбирают под экран — она заполняет его, как в
  // макете. Превью поста на титуле вписывается так же, как на слайде поста.
  const coverPromise =
    input.coverImage instanceof Blob
      ? prepareImage(input.coverImage, { maxSide: PHOTO_MAX_SIDE, keepAlpha: false }).catch((err) => {
          throw new Error(`Картинка титула не читается: ${err instanceof Error ? err.message : err}`);
        })
      : optionalScreenImage(input.coverImage, photos, blobs);
  const sourceOf = (post: (typeof input.posts)[number]) => post.imageFile ?? post.imageUrl ?? null;
  const postImages = input.posts.map((p) => optionalScreenImage(sourceOf(p) || null, photos, blobs));
  // На экране статистики превью стоит как в приложении — без размытых полей,
  // поэтому та же картинка готовится второй раз, маленькой и без подложки.
  const postThumbs = input.posts.map((p) => optionalThumb(sourceOf(p) || null, thumbs, blobs));
  const shots = input.articles.map((a) =>
    prepareImage(a.screenshot, { maxSide: SCREENSHOT_MAX_SIDE, keepAlpha: false }),
  );

  const [logo, cover, posts, thumbnails, screenshots] = await Promise.all([
    logoPromise.catch((err) => {
      throw new Error(`Логотип не читается: ${err instanceof Error ? err.message : err}`);
    }),
    coverPromise,
    Promise.all(postImages),
    Promise.all(postThumbs),
    Promise.all(shots).catch((err) => {
      throw new Error(`Скриншот статьи не читается: ${err instanceof Error ? err.message : err}`);
    }),
  ]);

  return { assets, logo, cover, posts, thumbnails, screenshots };
}

export async function generatePresentationPdf(
  input: PresentationInput,
  onProgress: (message: string) => void = () => {},
): Promise<Blob> {
  const [{ jsPDF }, prepared] = await Promise.all([
    import('jspdf'),
    preparePresentation(input, onProgress),
  ]);

  onProgress('Сборка PDF...');
  const { renderPresentation } = await import('./render-presentation');
  const doc = renderPresentation(
    jsPDF,
    {
      logo: prepared.logo,
      period: input.period,
      cover: prepared.cover,
      posts: input.posts.map((p, i) => ({
        badge: p.badge,
        url: p.url,
        username: p.username,
        views: p.views,
        reach: p.reach,
        likes: p.likes,
        comments: p.comments,
        saved: p.saved,
        shares: p.shares,
        reposts: p.reposts,
        totalInteractions: p.totalInteractions,
        profileVisits: p.profileVisits,
        follows: p.follows,
        avgWatchMs: p.avgWatchMs,
        totalWatchMs: p.totalWatchMs,
        skipRate: p.skipRate,
        image: prepared.posts[i],
        thumb: prepared.thumbnails[i],
      })),
      articles: input.articles.map((a, i) => ({
        screenshot: prepared.screenshots[i],
        domain: a.domain,
        url: a.url,
      })),
      contact: input.contact,
    },
    prepared.assets,
  );
  return doc.output('blob');
}
